import React from 'react';
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  SmartToy,
  AutoMode,
  Psychology,
  TrendingUp,
  Error,
  Pause,
  Schedule,
  Assessment,
} from '@mui/icons-material';

interface BotStatusIndicatorProps {
  status: 'active' | 'processing' | 'idle' | 'offline' | 'learning';
  confidence?: number;
  variant?: 'compact' | 'detailed' | 'card';
  showMetrics?: boolean;
  lastActivity?: string;
  activeConversations?: number;
  averageResponseTime?: number;
  successRate?: number;
}

const BotStatusIndicator: React.FC<BotStatusIndicatorProps> = ({
  status,
  confidence = 0,
  variant = 'compact',
  showMetrics = false,
  lastActivity = 'Just now',
  activeConversations = 0,
  averageResponseTime = 1.2,
  successRate = 94.5,
}) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          color: '#4CAF50',
          bgcolor: '#E8F5E9',
          icon: <SmartToy />,
          label: 'AI Bot Active',
          description: 'Processing conversations and qualifying leads',
        };
      case 'processing':
        return {
          color: '#2196F3',
          bgcolor: '#E3F2FD',
          icon: <Psychology />,
          label: 'AI Processing',
          description: 'Analyzing conversation and determining next steps',
        };
      case 'idle':
        return {
          color: '#FF9800',
          bgcolor: '#FFF3E0',
          icon: <Pause />,
          label: 'AI Idle',
          description: 'Bot is ready but no active conversations',
        };
      case 'offline':
        return {
          color: '#F44336',
          bgcolor: '#FFEBEE',
          icon: <Error />,
          label: 'AI Offline',
          description: 'Bot is temporarily unavailable',
        };
      case 'learning':
        return {
          color: '#9C27B0',
          bgcolor: '#F3E5F5',
          icon: <AutoMode />,
          label: 'AI Learning',
          description: 'Bot is being trained on new conversation patterns',
        };
      default:
        return {
          color: '#757575',
          bgcolor: '#F5F5F5',
          icon: <SmartToy />,
          label: 'AI Status Unknown',
          description: 'Unable to determine bot status',
        };
    }
  };

  const statusConfig = getStatusConfig(status);

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 85) return '#4CAF50';
    if (confidence >= 70) return '#FF9800';
    return '#F44336';
  };

  const renderCompact = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title={statusConfig.description}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: statusConfig.color,
            animation: status === 'active' || status === 'processing' ? 'pulse 2s infinite' : 'none',
          }}
        />
      </Tooltip>
      {confidence > 0 && (
        <Typography variant="caption" sx={{ color: getConfidenceColor(confidence), fontWeight: 600 }}>
          {confidence}%
        </Typography>
      )}
    </Box>
  );

  const renderDetailed = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: statusConfig.color }}>
          {status === 'processing' ? (
            <CircularProgress size={16} sx={{ color: statusConfig.color }} />
          ) : (
            statusConfig.icon
          )}
        </Box>
        <Typography variant="body2" fontWeight={600}>
          {statusConfig.label}
        </Typography>
      </Box>

      {confidence > 0 && (
        <Chip
          label={`${confidence}% confidence`}
          size="small"
          sx={{
            bgcolor: getConfidenceColor(confidence),
            color: 'white',
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
      )}

      {showMetrics && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <Schedule sx={{ fontSize: 12, mr: 0.5 }} />
            {lastActivity}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <TrendingUp sx={{ fontSize: 12, mr: 0.5 }} />
            {activeConversations} active
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderCard = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: statusConfig.color }}>
              {status === 'processing' ? (
                <CircularProgress size={24} sx={{ color: statusConfig.color }} />
              ) : (
                statusConfig.icon
              )}
            </Box>
            <Typography variant="h6" fontWeight={600}>
              AI Bot Status
            </Typography>
          </Box>
          <Chip
            label={statusConfig.label}
            sx={{
              bgcolor: statusConfig.bgcolor,
              color: statusConfig.color,
              fontWeight: 600,
            }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {statusConfig.description}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#F5F5F5', borderRadius: 1 }}>
              <Typography variant="h4" sx={{ color: getConfidenceColor(confidence) }}>
                {confidence}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Confidence
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#F5F5F5', borderRadius: 1 }}>
              <Typography variant="h4" color="primary">
                {activeConversations}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active Chats
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#F5F5F5', borderRadius: 1 }}>
              <Typography variant="h4" sx={{ color: '#4CAF50' }}>
                {averageResponseTime}s
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg Response
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: '#F5F5F5', borderRadius: 1 }}>
              <Typography variant="h4" sx={{ color: '#FF9800' }}>
                {successRate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Success Rate
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Last activity: {lastActivity}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment sx={{ fontSize: 16, color: '#616161' }} />
            <Typography variant="caption" color="text.secondary">
              Learning continuously
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Add CSS for pulse animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  switch (variant) {
    case 'compact':
      return renderCompact();
    case 'detailed':
      return renderDetailed();
    case 'card':
      return renderCard();
    default:
      return renderCompact();
  }
};

export default BotStatusIndicator;